import { Alert, AlertTitle, Typography } from '@mui/material';
import FlexContainer from 'components/FlexContainer';
import { ClaimCard } from 'features/fact-grabbing/ClaimCard';
import { ScrollToTopButton } from 'features/fact-grabbing/ScrollToTopButton';
import FormattedMessage from 'features/i18n/FormattedMessage';
import { LoadingAnimationWithAlternatingMessages } from 'features/loading-spinner/LoadingAnimationWithAlternatingMessages';
import { memo, useRef } from 'react';
import { ApiClaim } from 'services/backofficeIntegration/http/endpoints/fact-grabbing/httpGetClaimsByText';
import styled from 'styled-components';
import { withTestId } from 'utils/utils';

type Props = {
  claims: ApiClaim[];
  isLoading: boolean;
  isError: boolean;
  onClaimClick: (claim: ApiClaim) => void;
};

export const ClaimList = memo(({ claims, isLoading, isError, onClaimClick }: Props) => {
  const claimListRef = useRef<HTMLDivElement>(null);

  return (
    <>
      {isLoading && (
        <StatusInfoContainer {...withTestId('check-claims-loading')}>
          <LoadingAnimationWithAlternatingMessages animation="circle2" height={300} />
        </StatusInfoContainer>
      )}

      {isError && (
        <Alert severity="info" color="error" {...withTestId('check-claims-error')}>
          <AlertTitle>
            <FormattedMessage id="aiWriter.inspirations.factGrabbing.initial_view.error_title_analysis_failed" />
          </AlertTitle>

          <FormattedMessage id="aiWriter.inspirations.factGrabbing.error_extract_claims_failed" />
        </Alert>
      )}

      {!isLoading && !isError && claims.length === 0 && (
        <Alert severity="info" color="error" {...withTestId('check-claims-empty')}>
          <AlertTitle>
            <FormattedMessage id="aiWriter.inspirations.factGrabbing.initial_view.error_title_analysis_unavailable" />
          </AlertTitle>

          <FormattedMessage id="aiWriter.inspirations.factGrabbing.claim_list.empty" />
        </Alert>
      )}

      <ClaimListContainer ref={claimListRef} {...withTestId('check-claims-container')}>
        {!isLoading && !isError && claims.length > 0 && (
          <>
            {claims.map(claim => (
              <ClaimCard
                key={claim.id}
                claim={claim}
                onClick={() => onClaimClick(claim)}
                showReviewButton={true}
                isClickable={true}
              />
            ))}

            <ScrollToTopButton scrollContainerRef={claimListRef} />
          </>
        )}
      </ClaimListContainer>
    </>
  );
});

const StatusInfoContainer = styled(FlexContainer).attrs({
  direction: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'large'
})`
  margin-left: ${({ theme }) => theme.spacings.four};
`;

const ClaimListContainer = styled(FlexContainer).attrs({
  direction: 'column',
  gap: 0
})`
  position: relative;
  width: 100%;
  margin-top: ${({ theme }) => theme.spacings.two};

  overflow-y: auto;
  // The pixel value is roughly the height of the header
  max-height: calc(100vh - 300px);
  // Gave some spacings to the right to avoid border or box-shadow touching the scrollbar
  padding-right: ${({ theme }) => theme.spacings.one};
`;
